import { useEffect, useRef, useState } from 'react';
import type { FleetBoardCard, FleetBoardLane } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { BoardCard, type DropSide } from '@/components/fleet/board/board-card';

type Props = {
  readonly lane: FleetBoardLane;
  /** The all-projects view: per-project counts in the header, a project chip on each card. */
  readonly showProject: boolean;
  readonly now: number;
  /** Opens a card's details. */
  readonly onOpen?: ((card: FleetBoardCard) => void) | undefined;
  /** Opens the panel listing every worker of this lane. */
  readonly onWorkers?: ((lane: string) => void) | undefined;
  /**
   * Ordering the queue by hand. Absent when the operator may not command; the queue is then a plain list.
   * `busy`: a rank request for this lane is in flight, so its cards stay put until it settles.
   */
  readonly ranking?:
    | {
        readonly busy: boolean;
        readonly onDragStart: (lane: FleetBoardLane) => void;
        readonly onDragCancel: (lane: FleetBoardLane) => void;
        readonly onMove: (lane: FleetBoardLane, from: number, to: number) => void;
      }
    | undefined;
};

/** Where the dragged card would land: on which card, and on which side of it. */
type Over = { readonly identifier: string; readonly side: DropSide };

/** The index a card at `from` ends up at when dropped on `side` of the card at `target`. */
function landing(from: number, target: number, side: DropSide): number {
  const slot = target + (side === 'below' ? 1 : 0);

  return slot > from ? slot - 1 : slot;
}

/** A fleet lane the way `fleet-agent agent preview` lists it: header, rounds, stranded, queue, then what it holds back. */
export function BoardColumn({ lane, showProject, now, onOpen, onWorkers, ranking }: Props) {
  const t = useTranslations('pages.admin.fleet-board');
  const free = lane.projects.reduce((sum, { free: n }) => sum + n, 0);
  const running = lane.projects.reduce((sum, { running: n }) => sum + n, 0);
  const next = lane.queue[0];
  const single = lane.projects.length === 1 ? lane.projects[0] : undefined;
  const perProject =
    showProject && lane.projects.length > 1
      ? lane.projects
          .filter(({ free: n }) => n > 0)
          .map(({ project, free: n }) => `${project} ${String(n)}`)
          .join(' · ')
      : '';
  const prFirst = lane.projects.some((project) => project.prFirst);
  const flow =
    single === undefined
      ? ''
      : [
          single.flow.running === null
            ? single.flow.states.join(', ')
            : t('flow', { states: single.flow.states.join(', '), running: single.flow.running }),
          single.flow.with.length > 0 ? t('flow-with', { labels: single.flow.with.join(', ') }) : null,
          single.flow.without.length > 0 ? t('flow-without', { labels: single.flow.without.join(', ') }) : null,
        ]
          .filter((part) => part !== null)
          .join(' · ');
  // A rank belongs to one project and one lane, so a lane merged from several projects cannot be ordered.
  const draggable = ranking !== undefined && single !== undefined && lane.queue.length > 1;
  const mixed = ranking !== undefined && lane.projects.length > 1 && lane.queue.length > 1;
  // The browser's own drag and drop, as on the LandIQ board — no library. The grip is the keyboard way: the arrow
  // keys move the card one place, and the live region says where it landed.
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<Over | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [stepped, setStepped] = useState<string | null>(null);
  const dropped = useRef(false);
  const section = useRef<HTMLElement>(null);
  const total = lane.queue.length;

  // A keyed card moved in the list can lose focus with its node; the grip that moved it keeps it.
  useEffect(() => {
    if (stepped === null) return;

    const grip = section.current?.querySelector<HTMLElement>(`[data-drag-handle="${stepped}"]`);

    if (grip != null && document.activeElement !== grip) grip.focus();
  });

  const move = (identifier: string, from: number, to: number) => {
    if (ranking === undefined || from === to || to < 0 || to >= total) return;

    ranking.onMove(lane, from, to);
    setAnnouncement(t('dnd.moved', { ticket: identifier, position: to + 1, total }));
  };

  const dragOf = (card: FleetBoardCard, at: number) => ({
    dragging: dragging === card.identifier,
    dropSide:
      dragging !== null && dragging !== card.identifier && over?.identifier === card.identifier ? over.side : null,
    locked: ranking?.busy ?? false,
    accepting: dragging !== null,
    onDragStart: () => {
      dropped.current = false;
      setStepped(null);
      setDragging(card.identifier);
      ranking?.onDragStart(lane);
    },
    onDragEnd: () => {
      if (!dropped.current) ranking?.onDragCancel(lane);

      setDragging(null);
      setOver(null);
    },
    onDragOver: (side: DropSide) => {
      if (over?.identifier !== card.identifier || over.side !== side) setOver({ identifier: card.identifier, side });
    },
    onDrop: (side: DropSide) => {
      const from = lane.queue.findIndex(({ identifier }) => identifier === dragging);
      const to = from < 0 ? -1 : landing(from, at, side);

      setOver(null);
      setDragging(null);

      if (from < 0 || to === from) return;

      dropped.current = true;
      move(dragging as string, from, to);
    },
    onStep: (by: -1 | 1) => {
      setStepped(card.identifier);
      move(card.identifier, at, at + by);
    },
  });
  const empty = lane.rounds.length + lane.stranded.length + lane.queue.length + lane.untaken.length === 0;

  return (
    <section
      ref={section}
      aria-label={lane.name}
      className="flex min-w-0 snap-start flex-col gap-2 rounded-md bg-canvas p-2"
    >
      {/* Rests and sticks at the board's top edge, widened over half the column gap each side and of one height (its optional
          lines always keep their row), so the headers read as one band with one shadow line under it. */}
      <header className="sticky top-0 z-[1] -mx-3.5 -mt-2 flex flex-col gap-1 bg-canvas px-3.5 pb-2 pt-4 after:pointer-events-none after:absolute after:inset-x-0 after:top-full after:h-2 after:bg-gradient-to-b after:from-black/[0.06] after:to-transparent after:opacity-0 after:transition-opacity group-data-[scrolled]/board:after:opacity-100">
        <h2 className="flex flex-wrap items-center gap-x-2 text-base-sm font-semibold text-text-primary">
          <span className="font-mono">{lane.name}</span>
          <span className="text-xs font-medium text-success-700">{t('free', { n: free })}</span>
          {running > 0 && <span className="text-xs font-medium text-info-700">{t('running', { n: running })}</span>}
          {onWorkers !== undefined && (
            <button
              type="button"
              onClick={() => onWorkers(lane.name)}
              aria-label={t('workers.button-label', { lane: lane.name })}
              className="ml-auto text-xs font-medium text-text-tertiary underline-offset-2 hover:text-text-primary hover:underline"
            >
              {t('workers.button')}
            </button>
          )}
        </h2>
        <p
          className="min-h-4 truncate text-xs text-text-tertiary"
          title={[flow, perProject].filter(Boolean).join(' · ') || undefined}
        >
          {[flow, perProject].filter((part) => part !== '').join(' · ')}
        </p>
        <p className="min-h-4 truncate text-xs text-text-secondary">
          {next !== undefined && (
            <>
              {t('next')} <span className="font-mono font-semibold text-success-700">{next.identifier}</span>
              {showProject ? ` (${next.project})` : ''}
            </>
          )}
        </p>
      </header>
      {mixed && <p className="text-xs text-text-tertiary">{t('drag-multi-project')}</p>}
      {prFirst && <p className="text-xs text-text-tertiary">{t('pr-first')}</p>}
      {empty && (
        <div className="flex h-16 items-center justify-center rounded-lg border border-dashed border-border text-xs text-text-tertiary">
          {t('nothing')}
        </div>
      )}
      {lane.rounds.length + lane.stranded.length > 0 && (
        <ul className="flex flex-col gap-2">
          {lane.rounds.map((card) => (
            <BoardCard key={card.identifier} card={card} now={now} showProject={showProject} onOpen={onOpen} />
          ))}
          {lane.stranded.map((card) => (
            <BoardCard key={card.identifier} card={card} now={now} showProject={showProject} onOpen={onOpen} />
          ))}
        </ul>
      )}
      {draggable ? (
        <ul className="flex flex-col gap-2">
          {lane.queue.map((card, at) => (
            <BoardCard
              key={card.identifier}
              card={card}
              now={now}
              showProject={showProject}
              position={at + 1}
              next={at === 0}
              onOpen={onOpen}
              drag={dragOf(card, at)}
            />
          ))}
        </ul>
      ) : (
        <ul className="flex flex-col gap-2">
          {lane.queue.map((card, at) => (
            <BoardCard
              key={card.identifier}
              card={card}
              now={now}
              showProject={showProject}
              position={at + 1}
              next={at === 0}
              onOpen={onOpen}
            />
          ))}
        </ul>
      )}
      {draggable && (
        <p aria-live="polite" className="sr-only">
          {announcement}
        </p>
      )}
      {lane.untaken.length > 0 && (
        <>
          <div
            role="separator"
            className="mt-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-text-tertiary"
          >
            <span className="flex-none">{t('not-taken')}</span>
            <span className="h-px flex-1 bg-border" />
          </div>
          <ul className="flex flex-col gap-2">
            {lane.untaken.map((card) => (
              <BoardCard key={card.identifier} card={card} now={now} showProject={showProject} onOpen={onOpen} />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
